import React, { useCallback, useContext, useMemo } from 'react';
import type { GeneralChartProps } from './types';
import { FactorTrendLineChart } from './factor-line/FactorTrendLineChart';
import { ThemeContext } from 'styled-components';
import { FactorTrendNoData } from './FactorTrendNoData';
import { parseLineChartItems, convertLineDataToExcel } from './factor-line/factorLineUtils';
import useExportUpdate from '../../logic/useExportUpdate';
import { useRecoilValue } from 'recoil';
import { blockDisplayHeader, allProxyInfoState } from 'venn-state';
import { useGetRequestSubjectFee } from '../../../blocks/utils/hooks/use-advisory-fee';
import { useGetBlockSubjectSuperscripts } from '../../../blocks/components/core/block-subject-footnotes-and-superscripts';

export const FactorTrendLineChartParent = ({
  metrics = [],
  analyses = [],
  requests,
  selectedBlock,
  factorMetrics,
  exportMetaData,
  selectedRefId,
}: GeneralChartProps) => {
  const theme = useContext(ThemeContext);
  const title = useRecoilValue(blockDisplayHeader(selectedRefId));
  const allProxy = useRecoilValue(allProxyInfoState);
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const currentMetric = metrics[0];
  const showAsPercentage = currentMetric?.dataType === 'PERCENTAGE';

  const baseItems = useMemo(
    () =>
      parseLineChartItems({
        analyses,
        requests,
        selectedBlock,
        factorMetrics,
        colors: theme.Colors,
        currentMetric,
        allProxy,
        getRequestSubjectFee,
      }),
    [analyses, requests, selectedBlock, factorMetrics, theme.Colors, currentMetric, allProxy, getRequestSubjectFee],
  );

  const items = useMemo(
    () =>
      baseItems.map((item) => {
        if (!item.subject) return item;
        const superscript = getBlockSubjectSuperscripts({
          subject: item.subject,
          isBenchmark: !!item.isBenchmark,
        });
        if (!superscript) return item;
        return {
          ...item,
          seriesTypes: {
            ...item.seriesTypes,
            superscript,
          },
        };
      }),
    [baseItems, getBlockSubjectSuperscripts],
  );

  const excelDataFn = useCallback(
    () => convertLineDataToExcel(items, title, currentMetric?.dataType === 'PERCENTAGE') || [],
    [items, title, currentMetric?.dataType],
  );

  useExportUpdate({ selectedRefId, exportMetaData, excelDataFn });

  return (
    <div style={{ flex: 1, minWidth: 0 }} data-testid="qa-factor-trend-line-chart-parent">
      {!analyses.length ? (
        <FactorTrendNoData />
      ) : (
        <FactorTrendLineChart items={items} displayOptions={{ percentageValue: showAsPercentage }} />
      )}
    </div>
  );
};
